import './AthleteCardTop.css';

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

interface AthleteCardTopProps {
  name: string;
  number: string;
  position: string;
  returningstatus?: string | null;
  imageSrc: string | null;
  compact: boolean;
  onPhotoClick?: () => void;
  rightSlot?: React.ReactNode;
}

const AthleteCardTop: React.FC<AthleteCardTopProps> = ({
  name,
  number,
  position,
  returningstatus,
  imageSrc,
  compact,
  onPhotoClick,
  rightSlot,
}) => {
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  const imgClassName = compact
    ? 'athlete-card-top-photo-img athlete-card-top-photo-img--compact'
    : 'athlete-card-top-photo-img';
  const [imageLoading, setImageLoading] = useState(Boolean(imageSrc));
  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    setImageLoading(Boolean(imageSrc));
  }, [imageSrc]);

  /**
   * If the browser already had this src cached, the <img> can finish loading
   * before React attaches onLoad, leaving imageLoading stuck on true forever.
   * After src changes, check `complete && naturalWidth > 0` and clear manually.
   */
  useEffect(() => {
    if (!imageSrc) return;
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0) {
      setImageLoading(false);
    }
  }, [imageSrc]);

  const handleImgRef = useCallback((node: HTMLImageElement | null): void => {
    imgRef.current = node;
    if (node && imageSrc && node.complete && node.naturalWidth > 0) {
      setImageLoading(false);
    }
  }, [imageSrc]);

  return (
    <div className={`athlete-card-top${compact ? ' athlete-card-top--compact' : ''}`}>
      <div className="athlete-card-top-photo-wrap">
        <div className="athlete-card-top-photo">
          {imageSrc ? (
            onPhotoClick ? (
              <button
                type="button"
                className="athlete-card-top-photo-btn"
                onClick={onPhotoClick}
                onContextMenu={(e) => e.preventDefault()}
                aria-label="Open Photo"
              >
                <>
                  <span
                    className={`athlete-card-top-photo-skeleton${imageLoading ? '' : ' athlete-card-top-photo-skeleton--hidden'}`}
                    aria-hidden="true"
                  />
                  <img
                    ref={handleImgRef}
                    className={`${imgClassName}${imageLoading ? ' athlete-card-top-photo-img--loading' : ''}`}
                    src={imageSrc}
                    alt={name}
                    draggable={false}
                    onLoad={() => setImageLoading(false)}
                    onError={() => setImageLoading(false)}
                  />
                </>
              </button>
            ) : (
              <>
                <span
                  className={`athlete-card-top-photo-skeleton${imageLoading ? '' : ' athlete-card-top-photo-skeleton--hidden'}`}
                  aria-hidden="true"
                />
                <img
                  ref={handleImgRef}
                  className={`${imgClassName}${imageLoading ? ' athlete-card-top-photo-img--loading' : ''}`}
                  src={imageSrc}
                  alt={name}
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  onLoad={() => setImageLoading(false)}
                  onError={() => setImageLoading(false)}
                />
              </>
            )
          ) : (
            <span className="athlete-card-top-initial">{initial}</span>
          )}
        </div>
      </div>
      <div className="athlete-card-top-meta">
        <h2 className="athlete-card-top-name">{name}</h2>
        <span className="athlete-card-top-number">#{number}</span>
        <div className="athlete-card-top-pills">
          {returningstatus ? (
            <span className="athlete-card-top-pill">{returningstatus}</span>
          ) : null}
          <span className="athlete-card-top-pill">{position}</span>
        </div>
      </div>
      {rightSlot ? (
        <div className="athlete-card-top-right">{rightSlot}</div>
      ) : null}
    </div>
  );
};

export default AthleteCardTop;
